/**
 * Pure date helpers for DatePicker.
 *
 * Kept out of the component so they can be tested — vitest here has no React
 * rendering setup, and these are the parts that decide what value gets stored.
 */

/** Drop the time portion so comparisons are day-to-day. */
export const stripTime = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** The stored wire format for a single date. */
export const toIsoDate = (date) => {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Parse what someone typed into the date box.
 *
 * Accepts yyyy-mm-dd, m/d/yyyy (US order, single or double digits), a bare
 * 4-digit year, a month name, and anything else Date can read. Returns null
 * when it cannot be read as a date, so the caller can leave the value alone.
 */
export const parseTypedDate = (raw, { now = new Date() } = {}) => {
  const input = String(raw ?? '').trim();
  if (!input) return null;

  // Build from explicit parts where the format is known, so an impossible day
  // can be rejected rather than rolled over. Date turns 2/31 into 3 March, and
  // storing that would put a date on the record that nobody typed.
  const fromParts = (y, m, d) => {
    const built = new Date(y, m - 1, d);
    const rolledOver = built.getFullYear() !== y || built.getMonth() !== m - 1 || built.getDate() !== d;

    return rolledOver ? null : stripTime(built);
  };

  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(input)) {
    const [y, m, d] = input.split(/[-/]/).map(Number);
    return fromParts(y, m, d);
  }

  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(input)) {
    // US order: month first.
    const [m, d, y] = input.split(/[-/]/).map(Number);
    return fromParts(y, m, d);
  }

  if (/^\d{4}$/.test(input)) return stripTime(new Date(Number(input), 0, 1));

  let parsed = null;

  if (/^[a-zA-Z]+$/.test(input)) {
    parsed = new Date(`${input} 1, ${now.getFullYear()}`);
  } else {
    parsed = new Date(input);
  }

  if (!parsed || Number.isNaN(parsed.getTime())) return null;

  return stripTime(parsed);
};

/**
 * Move an already-chosen date onto a different month/year, keeping the day.
 *
 * This is what makes changing the year dropdown from 2026 to 2028 actually
 * change the stored date instead of only scrolling the calendar (OL-90). The
 * day is clamped to the length of the target month, so 31 Jan → Feb lands on
 * the 28th or 29th rather than rolling into March.
 */
export const reanchorToMonth = (isoDate, monthDate) => {
  if (!isoDate || !monthDate || Number.isNaN(monthDate.getTime())) return null;

  const [year, month, day] = String(isoDate).split('-').map(Number);
  if (!year || !month || !day) return null;

  const targetYear = monthDate.getFullYear();
  const targetMonth = monthDate.getMonth();

  if (targetYear === year && targetMonth === month - 1) return null; // nothing moved

  const monthDays = new Date(targetYear, targetMonth + 1, 0).getDate();

  return toIsoDate(new Date(targetYear, targetMonth, Math.min(day, monthDays)));
};

/** Whether a date passes the picker's future/past restrictions. */
export const isDateAllowed = (date, { disableFuture, disablePast, today } = {}) => {
  if (!date) return false;

  const day = stripTime(date);
  const reference = today ? stripTime(today) : stripTime(new Date());

  if (disableFuture && day > reference) return false;
  if (disablePast && day < reference) return false;

  return true;
};
